<template>
  <div>
    <div>
      <section :class="['hero', 'is-bold', heroStripClass]">
        <div class="hero-body">
          <div class="container is-fluid">
            <div class="level">
              <div class="level-left">
                <div>
                  <p class="title">
                    <kyc-details-field
                      name="name"
                      display-name="Name"
                      :value="mainName"
                      :client-id="clientId"
                      :edit-allowed="!editNotAllowed"
                      :hide-label="true"
                      @value-changed="handleValueChanged"
                    />
                  </p>
                  <p class="subtitle">
                    <kyc-details-field
                      name="type"
                      display-name="Type"
                      :value="mainType"
                      :hide-label="true"
                      @value-changed="handleValueChanged"
                    />
                  </p>
                </div>
              </div>

              <div class="level-right">
                <b-tooltip label="Create linked form" type="is-dark">
                  <router-link
                    :to="{ name: 'new', query: { linked: 1 }, params: { kycId: this.kycId, jobNumber: this.jobNumber } }">
                    <b-icon icon="library-plus"/>
                  </router-link>
                </b-tooltip>
                <b-tooltip label="Generate email" type="is-dark">
                  <a href=""
                    class="header-icon"
                    title="Generate email"
                    @click.prevent="generateEmail">
                    <b-icon icon="email"/>
                  </a>
                </b-tooltip>
                <b-tooltip label="Delete form" type="is-dark">
                  <a href=""
                    class="header-icon"
                    title="Delete KYC"
                    v-if="$auth.check(['admin', 'superadmin'])"
                    @click.prevent="confirmDelete">
                    <b-icon icon="delete"/>
                  </a>
                </b-tooltip>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div class="column is-10 is-offset-1">

        <div class="level">
          <div class="level-left">
            <back-link/>
          </div>
        </div>

        <div>

          <div class="level" v-if="linkedForms && linkedForms.length > 0">
            <b-message type="is-warning">
              <div class="level">
                <div class="level-left">
                  <span class="linked-label">Linked forms:</span>
                  <div class="linked-list" v-for="form in linkedForms" :key="form.id">
                    <a :href="`/kyc/${form.id}`">{{form.name}}</a>
                  </div>
                </div>
              </div>
            </b-message>
          </div>

          <div class="level">
            <kyc-details-field
              name="createdBy"
              display-name="Created by"
              :value="createdBy"
            />

            <kyc-details-field
              name="modifiedBy"
              display-name="Modified by"
              :value="modifiedBy"
            />
          </div>

          <div class="level">

              <kyc-details-field
                name="jobNumber"
                display-name="Job Number"
                :value="jobNumber"
                :edit-allowed="true && !editNotAllowed"
                @value-changed="handleValueChanged"
              />

              <kyc-details-field
                name="fileManagerId"
                display-name="File Manager"
                :value="fileManagerId"
                :select-options="$store.state.staff"
                :edit-allowed="!editNotAllowed"
                @value-changed="handleValueChanged"
              />

              <kyc-details-field
                name="status"
                display-name="Status"
                :value="status"
                :select-options="$store.state.formStatuses"
                :edit-allowed="$auth.check(['admin', 'superadmin'])"
                @value-changed="handleValueChanged"
              />

          </div>

        </div>

        <b-tabs v-model="activeTab" type="is-boxed" expanded @input="onTabChange">
          <b-tab-item label="Info" icon="file-document">

            <div class="buttons is-right">

              <b-upload
                v-if="!mandateFileId"
                v-model="mandateLetterFile"
                @input="onMandateInput"
              >
                <a class="button is-primary">
                  <b-icon icon="upload"/>
                  <span>Click to upload mandate letter</span>
                </a>
              </b-upload>

              <template v-else>
                <b-field v-if="!editMandate">
                  <p class="control">
                    <button
                      class="button is-primary is-outlined"
                      @click.prevent="getLink(mandateFileId)"
                    >
                      Download mandate letter
                    </button>
                 </p>
                  <p class="control">
                    <button class="button is-primary"
                      @click="handleEditMandate"
                    >
                      <b-icon icon="pencil"/>
                    </button>
                  </p>
                </b-field>

                <b-field v-if="editMandate">
                  <b-select placeholder="Select a file" v-model="newMandateFileId">
                    <option
                      v-for="f in files"
                      :value="f.id"
                      :key="f.id">
                      {{ f.name }}
                    </option>
                  </b-select>
                  <p class="control">
                    <button class="button is-primary"
                      @click="confirmEditMandate"
                    >
                      <b-icon icon="content-save"/>
                    </button>
                  </p>
                  <p class="control">
                    <button class="button is-primary"
                      @click="discardEditMandate"
                    >
                      <b-icon icon="close"/>
                    </button>
                  </p>
                </b-field>
              </template>

              <button class="button is-primary is-outlined"
                :disabled="editNotAllowed"
                @click="isModalActive = true">
                <b-icon icon="plus"/>
                <span>Add new person / company</span>
              </button>
            </div>

            <b-modal :active.sync="isModalActive" has-modal-card>
              <modal-add-entity
                v-if="entitiesList"
                :companies="entitiesList.company"
                :kycId="kycId"
                :total-shares="totalShares"
                @kyc-updated="getData"
              />
            </b-modal>

             <kyc-details-table
              :type="'main'"
              :table-data="mainCompany"
              @cell-changed="cellChangeStatus"
              @names-updated="updateNames"
              @update-tree="updateTree"
              @delete-role="deleteRole"
              @delete-entity="deleteEntity"
            />

            <kyc-details-table
              :type="'person'"
              :table-data="individuals"
              :show-shares="allowShowShares"
              @cell-changed="cellChangeStatus"
              @names-updated="updateNames"
              @shares-num-updated="updateSharesNumber"
              @update-tree="updateTree"
              @delete-role="deleteRole"
              @delete-entity="deleteEntity"
            />

            <kyc-details-table
              :type="'holding'"
              :table-data="holdingCompanies"
              :show-shares="allowShowShares"
              @cell-changed="cellChangeStatus"
              @names-updated="updateNames"
              @shares-num-updated="updateSharesNumber"
              @update-tree="updateTree"
              @delete-role="deleteRole"
              @delete-entity="deleteEntity"
            />

          </b-tab-item>

          <b-tab-item label="Files" icon="file-multiple">
            <template slot="header">
                <b-icon icon="file-multiple"/>
                <span>
                  Files
                  <b-tag rounded v-if="files.length > 0">{{files.length}}</b-tag>
                </span>
            </template>
            <kyc-details-files
              :kyc-id="kycId"
              :files="files"
              :edit-not-allowed="editNotAllowed"
              @file-deleted="handleFileDeleted"
              @files-updated="handleFilesUpdated"
            />
          </b-tab-item>

          <b-tab-item label="Searches" icon="magnify">
            <kyc-search-results
              v-model="checks.world_check"
              :kyc-id="kycId"
              :id="'world_check'"
              :edit-not-allowed="editNotAllowed"
              @input="onSearchesUpdate"
            />

            <kyc-search-results
              v-model="checks.internet"
              :kyc-id="kycId"
              :id="'internet'"
              :edit-not-allowed="editNotAllowed"
              @input="onSearchesUpdate"
            />

            <kyc-search-results
              v-model="checks.other"
              :kyc-id="kycId"
              :id="'other'"
              :edit-not-allowed="editNotAllowed"
              @input="onSearchesUpdate"
            />
          </b-tab-item>

          <b-tab-item label="Chart" icon="sitemap">
            <diagram :treeData="treeData" v-if="diagramTabIsActive"/>
          </b-tab-item>

          <b-tab-item label="Notes">
            <template slot="header">
                <b-icon icon="message-text-outline" :type="notesTabIconType"/>
                <span>Notes</span>
            </template>

            <b-field>
              <b-input
                type="textarea"
                v-model="note"
                placeholder="Please add any notes about KYC form, status of documents etc"
              />
            </b-field>

            <div class="buttons is-right">
              <p class="control">
                <button class="button is-primary" @click="saveNotes" :disabled="editNotAllowed">
                  Add
                </button>
              </p>
            </div>

            <b-message
              v-for="(note, idx) in notesReversed"
              :closable="false"
              :title="`Created by ${note.createdBy} on ${note.createdDate}`"
              :key="`note_${idx}`"
            >
              <p>
                {{ note.content }}
              </p>
              <p class="notes__buttons" v-if="$auth.check(['admin', 'superadmin'])">
                <a href="" @click.prevent="deleteNoteHandler(idx)"><b-icon icon="delete"/></a>
              </p>
            </b-message>

          </b-tab-item>

        </b-tabs>

        <b-loading
          :is-full-page="true"
          :active.sync="$store.state.isLoading"
          :can-cancel="true"
        />

      </div>
    </div>
  </div>
</template>

<script>
import { getObject } from '@/utils'

import BackLink from '@/components/BackLink'
import KycDetailsField from '@/components/KycDetailsField'
import FileManagerSelect from '@/components/FileManagerSelect'
import KycDetailsTable from '@/components/KycDetailsTable'
import KycSearchResults from '@/components/KycSearchResults'
import KycDetailsFiles from '@/components/KycDetailsFiles'
import ModalAddEntity from '@/components/ModalAddEntity'
import Diagram from '@/components/Diagram/Diagram'

export default {
  name: 'KycDetails',
  // title: (context) => {
  //   console.log(context.mainName)
  //   `${context.jobNumber} - ${context.mainName}`
  // },
  components: {
    BackLink,
    KycDetailsField,
    FileManagerSelect,
    KycDetailsTable,
    KycSearchResults,
    KycDetailsFiles,
    ModalAddEntity,
    Diagram
  },
  data () {
    return {
      kycId: this.$route.params.kycId,
      entitiesList: null,
      treeData: null,
      clientId: null,
      status: null,
      jobNumber: null,
      fileManagerId: '',
      createdBy: null,
      modifiedBy: null,
      edit: null,
      activeTab: 0,
      checks: { world_check: {}, internet: {}, other: {} },
      files: [],
      isModalActive: false,
      diagramTabIsActive: false,
      notesTabIsActive: false,
      linkedForms: null,
      isSupplementary: false,
      note: null,
      notes: [],
      mandateLetterFile: null,
      editMandate: false,
      newMandateFileId: null,
      totalShares: null
    }
  },
  watch: {
    jobNumber () {
      this.setPageTitle()
    }
  },
  computed: {
    mainName () {
      if (this.mainCompany.length === 0 && this.individuals.length === 0) return null
      return this.mainCompany[0]
        ? this.mainCompany[0].name
        : this.individuals[0].name
    },
    mainType () {
      if (this.mainCompany.length === 0 && this.individuals.length === 0) return null
      const mainType = this.mainCompany[0]
        ? this.mainCompany[0].type
        : this.individuals[0].role[0]
      const mainTypeDesc = this.$store.getters.getCompanyTypeByTypeName(mainType)
      return mainTypeDesc || mainType
    },
    mainCompany () {
      if (!this.entitiesList || this.entitiesList.company.length === 0) return []
      const mainCompany = this.entitiesList.company.slice(0, 1)
      if (mainCompany) mainCompany[0].order = 0
      return mainCompany
    },
    individuals () {
      if (!this.entitiesList || this.entitiesList.individual.length === 0) return []
      let individuals = this.entitiesList.individual
      individuals = individuals.map((person, idx) => {
        person.order = idx
        return person
      })
      // return this.entitiesList.individual
      return individuals
    },
    holdingCompanies () {
      if (!this.entitiesList || this.entitiesList.company.length < 1) return []
      let holdings = this.entitiesList.company.slice(1)
      holdings = holdings.map((company, idx) => {
        company.order = idx
        return company
      })
      return holdings
    },
    isCompleted () {
      if (!this.entitiesList) return false
      let companyDocSts = this.getDocStatuses(this.entitiesList.company)
      let indDocSts = this.getDocStatuses(this.entitiesList.individual)

      let values = companyDocSts.concat(indDocSts)

      if (values.length > 0 &&
          values.indexOf(0) === -1 &&
          values.indexOf(10) === -1 &&
          this.searchesCompleted) {
        return true
      }
      return false
    },
    searchesCompleted () {
      let isSuccess = null
      const keys = Object.keys(this.checks)
      if (keys.length > 0) {
        isSuccess = this.checks[keys[0]].isSuccess
      }
      for (const key in this.checks) {
        isSuccess = isSuccess && this.checks[key].isSuccess
      }
      return isSuccess
    },
    notesTabIconType () {
      if (this.notes.length > 0) return 'is-danger'
      if (this.notesTabIsActive) return 'is-primary'
      return 'is-black'
    },
    heroStripClass () {
      if (this.isCompleted) return 'is-success'
      if (this.isSupplementary) return 'is-warning'
      return 'is-light'
    },
    notesReversed () {
      const notes = this.notes.slice()
      return notes.reverse()
    },
    editNotAllowed () {
      return this.status === 30
    },
    mandateFileId () {
      const file = this.files.find(file => {
        return file.is_mandate_letter
      })
      if (!file) return null
      return file.id
    },
    allowShowShares () {
      const exceptionKeywords = ['listed', 'individual', 'law firm']
      if (!this.mainType) return true
      return exceptionKeywords.every(el => {
        return this.mainType.toLowerCase().indexOf(el) === -1
      })
    }
  },
  methods: {
    onTabChange (idx) {
      this.diagramTabIsActive = idx === 3
      this.notesTabIsActive = idx === 4
    },
    getDocStatuses (entity) {
      if (!entity) return
      let statuses = entity.map(member => {
        return Object.values(member.requiredDocs)
      })

      if (statuses.length === 0) return []

      statuses = statuses.reduce((acc, el) => {
        return acc.concat(el)
      })

      return statuses
    },
    // updates document status in db
    cellChangeStatus (entity, row, docId, value) {
      const isCompleted = this.isCompleted
      const data = { entity, row, docId, value, isCompleted }
      this.axios.put(`/kyc/${this.kycId}/doc`, data)
        .then(response => {
          this.$toast.open({
            message: 'Document status changed successfully',
            type: 'is-info'
          })
          this.status = isCompleted ? 20 : 10
          this.getData()
        })
        .catch(error => {
          this.isLoading = false
          console.log(error)
          this.$toast.open({
            message: `Error: ${error.response.error ? error.response.error : error.message}`,
            type: 'is-danger'
          })
          this.getData()
        })
    },
    updateNames (data) {
      if (!data.hasOwnProperty('newTreeData')) {
        data.newTreeData = this.treeData
      }

      this.axios.put(`/kyc/${this.kycId}/names`, data)
        .then(response => {
          this.$toast.open({
            message: 'Names updated successfully',
            type: 'is-info'
          })
          if (this.edit) {
            this.edit.name = null
            this.edit.value = null
          }
          this.setPageTitle()
        })
        .catch(error => this.apiErrorHandler(error))
    },
    updateSharesNumber (data) {
      console.log(data)
      this.axios.put(`/kyc/${this.kycId}/shares`, data)
        .then(response => {
          this.$toast.open({
            message: 'Share number updated successfully',
            type: 'is-info'
          })
          this.getData()
        })
        .catch(error => this.apiErrorHandler(error))
    },
    getData () {
      this.$store.dispatch('setIsLoading', { isLoading: true })
      this.axios.get(`/kyc/${this.kycId}`)
        .then(response => {
          if (response.data) {
            this.entitiesList = response.data.list_data
            this.treeData = response.data.tree_data
            this.clientId = response.data.wfm_client_id
            this.jobNumber = response.data.job_number
            this.fileManagerId = response.data.file_manager_id
            this.status = response.data.status
            this.createdBy = response.data.created_by
            this.modifiedBy = response.data.modified_by
            this.checks = { ...this.checks, ...response.data.checks }
            this.files = response.data.files
            this.isSupplementary = response.data.is_supplementary
            this.linkedForms = response.data.linked_forms
            this.notes = response.data.notes
            this.$store.dispatch('setIsLoading', { isLoading: false })
            this.setPageTitle()
          } else {
            this.$router.go(-1)
          }
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: `Error: ${error.response.data.message}`,
            type: 'is-danger'
          })
          // this.$router.go(-1)
          this.$store.dispatch('setIsLoading', { isLoading: false })
          this.$router.push({ name: 'main' })
        })
    },
    deleteKyc () {
      this.axios.delete(`/kyc/${this.kycId}`)
        .then(response => {
          this.$toast.open({
            message: response.data.message,
            type: 'is-info'
          })
          this.$router.replace('/')
        })
        .catch(error => this.apiErrorHandler(error))
    },
    editName (value) {
      let oldName = null
      if (value) {
        let entityType = null
        if (this.mainCompany.length > 0) {
          oldName = this.mainCompany[0].name
          this.mainCompany[0].name = value
          entityType = 'main'
        } else if (this.individuals.length > 0) {
          oldName = this.individuals[0].name
          this.individuals[0].name = value
          entityType = 'person'
        } else {
          this.$toast.open({
            message: 'Can\'t find entity to change name',
            type: 'is-danger'
          })
          return
        }

        this.$set(this.treeData, 'name', value)
        // request to change db
        this.updateNames({entityType: entityType,
          oldName: oldName,
          newName: value,
          newTreeData: this.treeData
        })
      }
    },
    updateTree (eventData) {
      console.log(eventData)
      if (eventData.type.indexOf('director') > -1) {
        for (const key in this.treeData.directors) {
          if (this.treeData.directors.hasOwnProperty(key)) {
            const element = this.treeData.directors[key]
            console.log(element)
            if (element === eventData.oldName) {
              this.$set(this.treeData.directors, key, eventData.newName)
            }
          }
        }
      } else if (eventData.type.indexOf('representative') > -1) {
        this.$set(this.treeData, 'representativeName', eventData.newName)
      }
      const treeNode = getObject(this.treeData, 'name', eventData.oldName)
      if (treeNode) {
        this.$set(treeNode, 'name', eventData.newName)
      }
    },
    apiErrorHandler (error) {
      this.isLoading = false
      console.log(error)
      const message = error.response.data.error || error.response.data.message || error.message
      this.$toast.open({
        message: `Error: ${message}`,
        type: 'is-danger'
      })
    },
    handleValueChanged (edit) {
      if (!edit || !edit.name) return
      this.edit = edit
      if (['status', 'fileManagerId', 'jobNumber', 'clientId', 'notes'].indexOf(edit.name) > -1) {
        let data = { name: this.edit.name, value: this.edit.value }
        if (data.name === 'fileManagerId') data.name = 'file_manager_id'
        if (data.name === 'jobNumber') data.name = 'job_number'
        if (data.name === 'clientId') data.name = 'wfm_client_id'
        this.axios.put(`/kyc/${this.kycId}`, data)
          .then(response => {
            this.$toast.open({
              message: 'Form updated successfully',
              type: 'is-success'
            })
            this.$set(this.$data, this.edit.name, this.edit.value)
            this.edit.name = null
            this.edit.value = null
          })
          .catch(error => this.apiErrorHandler(error))
      }
      if (this.edit.name === 'name') this.editName(this.edit.value)
    },
    confirmDelete () {
      this.$dialog.confirm({
        message: 'Delete current KYC form?',
        onConfirm: () => this.deleteKyc()
      })
    },
    generateEmail () {
      this.axios.get(`/kyc/${this.kycId}/email`)
        .then(response => {
          const filename = `${this.jobNumber ? this.jobNumber : 'email'}.eml`
          this.downloadContent(filename, response.data)
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: error,
            type: 'is-danger'
          })
        })
    },
    downloadContent (filename, content) {
      const element = document.createElement('a')
      element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(content))
      element.setAttribute('download', filename)

      element.style.display = 'none'
      document.body.appendChild(element)

      element.click()

      document.body.removeChild(element)
    },
    saveNotes () {
      const createdDate = (new Date()).toISOString()
      const user = this.$auth.user()
      const createdBy = user.name
      const content = this.note
      const newNoteData = {
        createdDate,
        createdBy,
        content
      }
      this.notes.push(newNoteData)
      this.handleValueChanged({ name: 'notes', value: this.notes })
      this.note = null
    },
    getLink (fileId) {
      window.location.replace(
        `${this.axios.defaults.baseURL}/kyc/${this.kycId}/file/${fileId}`
      )
    },
    onMandateInput (file) {
      const reader = new FileReader()
      reader.onload = () => {
        this.uploadMandate(reader.result)
      }
      reader.readAsDataURL(file)
    },
    uploadMandate (content) {
      this.axios.post(`/kyc/${this.kycId}/upload`, {
        file_name: this.mandateLetterFile.name,
        file_type: this.mandateLetterFile.type,
        is_mandate_letter: true,
        content
      })
        .then(response => {
          this.files = response.data
          this.$toast.open({
            'message': 'File upload successful',
            'type': 'is-success'
          })
          this.mandateLetterFile = null
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            'message': error.message,
            'type': 'is-danger'
          })
        })
    },
    handleFileDeleted (fileId) {
      const fileIdx = this.files.findIndex(el => {
        return el.id === fileId
      })
      if (fileIdx === null) return
      this.files.splice(fileIdx, 1)
    },
    handleFilesUpdated (newFiles) {
      this.$set(this, 'files', newFiles)
    },
    handleEditMandate () {
      this.editMandate = true
      this.newMandateFileId = this.mandateFileId
    },
    discardEditMandate () {
      this.editMandate = false
      this.newMandateFileId = null
    },
    confirmEditMandate () {
      const oldFileIdx = this.files.findIndex(f => {
        return f.id === this.mandateFileId
      })
      if (oldFileIdx === -1) return
      this.$set(this.files[oldFileIdx], 'is_mandate_letter', false)
      const newFileIdx = this.files.findIndex(f => {
        return f.id === this.newMandateFileId
      })
      if (newFileIdx === -1) return
      this.$set(this.files[newFileIdx], 'is_mandate_letter', true)
      this.editMandate = false
      // update files on server
      this.updateFiles()
    },
    updateFiles () {
      this.axios.put(`kyc/${this.kycId}/files`, { files: this.files })
        .then(response => {
          console.log(response)
          this.$toast.open({
            message: response.data.message || response.data.msg,
            type: 'is-success'
          })
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: error.response.error || error.response.message || error.message,
            type: 'is-danger'
          })
        })
    },
    onSearchesUpdate () {
      const data = { name: 'checks', value: this.checks }
      this.axios.put(`/kyc/${this.kycId}`, data)
        .then(_ => {
          this.$toast.open({
            message: 'Searches data updated',
            type: 'is-info'
          })
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: error,
            type: 'is-danger'
          })
        })
    },
    deleteNoteHandler (noteIdx) {
      this.$dialog.confirm({
        message: 'Continue on removing the note?',
        onConfirm: () => this.deleteNote(noteIdx)
      })
    },
    deleteNote (noteIdx) {
      this.$delete(this.notes, this.notes.length - noteIdx - 1)
      this.handleValueChanged({ name: 'notes', value: this.notes })
    },
    deleteRole (personName, roleIdx) {
      this.axios.post(`kyc/${this.kycId}/role/delete`, { personName, roleIdx })
        .then(response => {
          this.getData()
          this.$toast.open({
            message: response.data.message,
            type: 'is-success'
          })
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: error.response.message,
            type: 'is-danger'
          })
        })
    },
    deleteEntity (entityName) {
      this.axios.post(`kyc/${this.kycId}/entity/delete`, { entityName })
        .then(response => {
          console.log(response)
          this.getData()
          this.$toast.open({
            message: response.data.message,
            type: 'is-success'
          })
        })
        .catch(error => {
          console.log(error)
          this.$toast.open({
            message: error.response.message,
            type: 'is-success'
          })
        })
    },
    setPageTitle () {
      this.$title = this.mainName
      if (this.jobNumber) {
        this.$title = `${this.jobNumber} - ${this.mainName}`
      }
    }
  },
  created () {
    if (this.kycId) {
      this.getData()
    }
  },
  mounted () {
    if (this.$store.state.staff.length === 0) {
      this.$store.dispatch('getStaff')
    }

    if (!this.$store.state.docTypesDesc) {
      this.$store.dispatch('getDocTypesDesc')
    }
  }
}
</script>

<style scoped>

.header-icon {
  margin-left: 1em;
}

.header-icon:hover {
  opacity: 0.7;
}

.b-tabs {
  margin-top: 2em;
}

.message {
  width: 100%;
}

.linked-label {
  font-weight: bold;
  margin-right: 0.5em;
}

.linked-list {
  display: flex;
}

.linked-list a {
  margin-right: 0.5em;
}

.buttons .field:not(:last-child) {
  margin-bottom: 0;
  margin-right: 1em;
}

.notes__buttons {
  display: flex;
  justify-content: flex-end;
}

</style>

<style>
.b-tabs .tab-content {
  min-height: 110vh;
}
</style>
